// CardForm.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FullPageLoader, Input } from '../components';
import { GetOnAppStore, GetOnPlayStore, MoneyIcon } from '../assets';
import { useDevices, useAppConfig, useRents, useSafe2Pay3DS } from '../hooks';
import { formatCurrency } from '../utils/number.utils';
import creditCardType from 'credit-card-type';

const APP_STORE_URL = 'https://apps.apple.com/br/app/firefly/id6472669753';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=br.com.fireflycharge&hl=en';

const schema = yup.object().shape({
    cardholder_name: yup.string().required('Nome do titular do cartão é obrigatório'),
    card_number: yup
        .string()
        .matches(
            /^\d{4}\s\d{4}\s\d{4}\s\d{4}$/,
            'O número do cartão deve conter 16 dígitos separados por espaços',
        )
        .required('Número do cartão é obrigatório'),
    card_expire_date: yup
        .string()
        .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, 'A data de validade deve estar no formato MM/AA')
        .required('Data de validade é obrigatória'),
    card_security_number: yup
        .string()
        .matches(/^\d{3,4}$/, 'O código de segurança deve ter 3 ou 4 dígitos')
        .required('Código de segurança é obrigatório'),
    cardholder_document: yup
        .string()
        .matches(/^\d{3}.\d{3}.\d{3}-\d{2}$/, 'O documento deve conter 9 dígitos')
        .required('Documento do titular do cartão é obrigatório'),
    phone: yup.string().required('Telefone é obrigatório'),
});

type FormData = yup.InferType<typeof schema>;

const Rent: React.FC = () => {
    const [device, setDevice] = useState<any>();
    const [searchParams] = useSearchParams();
    const sn = searchParams.get('t');
    const navigate = useNavigate();

    const [cvvLength, setCvvLength] = useState(3);
    const [cardBrand, setCardBrand] = useState('');

    const [rent, setRent] = useState<Rent>();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const { searchDevice } = useDevices();
    const { messages } = useAppConfig();
    const { isLoading, webRent, fetchRent } = useRents();
    const { authenticate } = useSafe2Pay3DS();

    useEffect(() => {
        if (!sn) return;
        (async () => {
            const deviceData = await searchDevice(sn);
            setDevice(deviceData as any);
        })();
        // eslint-disable-next-line
    }, [sn]);

    const {
        handleSubmit,
        formState: { errors },
        control,
        watch,
    } = useForm<FormData>({
        resolver: yupResolver(schema),
        defaultValues: {
            cardholder_name: '',
            card_number: '',
            card_expire_date: '',
            card_security_number: '',
            cardholder_document: '',
            phone: '',
        },
    });

    const onSubmit = async (data: FormData) => {
        setIsSubmitting(true);

        // console.log('on submit:', data);
        if (!data) {
            setIsSubmitting(false);
            return;
        }

        // @ts-ignore
        var safe2pay_visitor_id = window.visitorID;

        const exp = data.card_expire_date.split('/');
        const card_expire_date = `${exp[0]}/20${exp[1]}`; // convert 10/31 to 10/2031

        const external_authentication = await authenticate({
            cardnumber: data.card_number.replace(/[^\d]/g, ''),
            cardexpirationmonth: card_expire_date.split('/')[0],
            cardexpirationyear: card_expire_date.split('/')[1],
            cardalias: data.cardholder_name,
        });

        // console.log('Autenticação bem-sucedida', external_authentication);

        const payload = {
            device_id: device.id,
            ...data,
            card_expire_date,
            card_brand: cardBrand,
            card_number: data.card_number.replace(/[^\d]/g, ''),
            cardholder_document: data.cardholder_document.replace(/[^\d]/g, ''),
            phone: data.phone.replace(/[^\d]/g, ''),
            safe2pay_visitor_id,
            external_authentication,
        };

        // console.log('requesting...');
        const response = await webRent(payload);

        // console.log('result:', response?.rent);
        if (response?.rent) {
            setRent(response.rent);
        }

        setIsSubmitting(false);
    };

    // console.log({ cardBrand });

    const cost_first_hour = formatCurrency(device?.location?.charge_value_first_hour);
    const cost_per_hour = formatCurrency(device?.location?.charge_value_per_hour);

    const pricingMessage =
        cost_first_hour === cost_per_hour
            ? `${cost_per_hour} por hora`
            : `${cost_first_hour} na primeira hora depois ${cost_per_hour} por hora`;

    const rentFreeTimeMessage = useMemo(() => {
        if (device?.location?.charge_free_minutes) {
            return `Grátis ${device?.location?.charge_free_minutes} minutos`;
        }

        return '';
    }, [device]);

    const card_number = watch('card_number');

    const verifyCvvLength = () => {
        if (card_number?.length < 4) return;
        const ccType = creditCardType(card_number)?.[0];
        setCvvLength(ccType?.code?.size || 3);
        setCardBrand(ccType?.type || '');
    };

    useEffect(() => {
        verifyCvvLength();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [card_number]);

    const cvvMask = cvvLength === 4 ? '9999' : '999';

    const verifyRentStatus = async () => {
        if (!rent) return;
        const updatedRent = await fetchRent(rent.id);
        if (updatedRent.status === 'ACTIVE') {
            navigate('/success');
        } else if (updatedRent && updatedRent.status !== 'PENDING_PAYMENT') {
            setRent(updatedRent);
        }
    };

    useEffect(() => {
        if (!rent || rent?.status !== 'PENDING_PAYMENT') return;

        verifyRentStatus();

        const interval = setInterval(verifyRentStatus, 1000);

        return () => clearInterval(interval); // cleanup on unmount

        // eslint-disable-next-line
    }, [rent]);

    const displayLoading = isLoading || (rent && rent.status === 'PENDING_PAYMENT');

    const disableSubmit = !sn || !device || isSubmitting;

    return (
        <div className="flex flex-col justify-center items-center min-h-[100vh] w-100 bg-white p-6 text-gray-900">
            {/* <p className="mb-2 text-2xl font-bold">Instale o App</p>
            <p className="mb-6">
                <DeviceStoreComponent />
            </p> 
            <p className="mb-4 text-2xl font-bold">Ou alugue agora</p> */}
            <p className="mb-4 text-2xl font-bold">Alugue agora</p>
            {!sn && <p className="mb-4 font-bold text-red-500">Dispositivo não relecionado</p>}

            <div className="flex w-full px-6 py-4 mb-3 rounded-lg shadow-deep-faint">
                <div className="mr-6">
                    <MoneyIcon />
                </div>
                <div className="font-bold">
                    {rentFreeTimeMessage && (
                        <p className="text-primary-500">{rentFreeTimeMessage}</p>
                    )}
                    <p>{pricingMessage}</p>
                </div>
            </div>
            <p className="pb-4 mb-4 text-sm text-gray-800 border-b border-gray-200">
                {/* Se o power bank não for retornado em até 00 horas, será considerada compra no valor de R$0,00 */}
                {messages?.rent_description}
            </p>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="max-w-md mx-auto space-y-4 rounded-xl"
            >
                {/* {error && <p className="text-red-600 text-md">{error}</p>} */}
                <Input
                    control={control}
                    name="cardholder_name"
                    label="Nome do titular"
                    placeholder="Ex.: João da Silva"
                    error={errors.cardholder_name}
                />
                <Input
                    control={control}
                    name="card_number"
                    placeholder="Ex.: 1234 5678 9012 3456"
                    mask="9999 9999 9999 9999"
                    label="Número do cartão"
                    error={errors.card_number}
                />

                <div className="flex gap-5">
                    <Input
                        control={control}
                        name="card_expire_date"
                        placeholder="MM/AA"
                        mask="99/99"
                        label="Validade"
                        error={errors.card_expire_date}
                    />
                    <Input
                        control={control}
                        name="card_security_number"
                        placeholder="123"
                        mask={cvvMask}
                        label="CVV"
                        error={errors.card_security_number}
                    />
                </div>

                <div className="flex gap-5">
                    <Input
                        control={control}
                        name="cardholder_document"
                        placeholder="000.000.000-00"
                        mask="999.999.999-99"
                        label="CPF"
                        error={errors.cardholder_document}
                    />
                    <Input
                        control={control}
                        name="phone"
                        placeholder="(00) 91234-5678"
                        mask="(99) 99999-9999"
                        label="Telefone"
                        error={errors.phone}
                    />
                </div>

                <button
                    type="submit"
                    className="p-4 w-full font-bold rounded-[100px] bg-primary-500 text-white enabled:shadow-purple-glow disabled:bg-gray-400 disabled:cursor-not-allowed"
                    disabled={disableSubmit}
                >
                    Alugar
                </button>
                {displayLoading && <FullPageLoader />}
            </form>
        </div>
    );
};

const isAppleDevice = () => {
    // @ts-ignore
    const ua = navigator.userAgent || navigator.vendor || window.opera;

    return (
        /iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
};

const DeviceStoreComponent = () => {
    if (isAppleDevice()) {
        return (
            <Link to={APP_STORE_URL}>
                <GetOnAppStore className="w-[270px] h-[80px]" />
            </Link>
        );
    }

    return (
        <Link to={PLAY_STORE_URL}>
            <GetOnPlayStore className="w-[270px] h-[80px]" />
        </Link>
    );
};

export default Rent;
